import type { DoodFieldType } from './descriptor';

/**
 * Recursive path utilities used to derive nested object shapes from dotted
 * `name` declarations (e.g. `colors.backgroundColor`).
 */

export type Split<Path extends string> =
  Path extends `${infer Head}.${infer Rest}` ? [Head, ...Split<Rest>] : [Path];

export type BuildNested<Segments extends readonly string[], Leaf> =
  Segments extends [infer Only extends string]
    ? { [K in Only]: Leaf }
    : Segments extends [infer Head extends string, ...infer Tail extends readonly string[]]
      ? { [K in Head]: BuildNested<Tail, Leaf> }
      : never;

export type PathToNested<Path extends string, Leaf> = BuildNested<Split<Path>, Leaf>;

export type UnionToIntersection<U> =
  (U extends any ? (x: U) => void : never) extends (x: infer I) => void ? I : never;

/**
 * All dotted paths to leaf values in a nested config shape. Used to autocomplete
 * `fields: [...]` entries in the dashboard layout.
 */
export type DotPath<T, Prefix extends string = ''> =
  T extends PropertyValueLeaf
    ? Prefix extends ''
      ? never
      : Prefix
    : T extends DescriptorControlLeaf
      ? Prefix extends ''
        ? never
        : Prefix
      : T extends object
        ? {
            [K in keyof T & string]: Prefix extends ''
              ? DotPath<T[K], K>
              : DotPath<T[K], `${Prefix}.${K}`>
          }[keyof T & string]
        : never;

// Local leaf brands referenced by DotPath without importing the full union,
// kept in sync with the literal brands defined in `property-value.ts`.
type PropertyValueLeaf = { readonly __doodPropertyValue: string };
type DescriptorControlLeaf = { readonly __doodDescriptorControlField: string };

export type DoodConfigLeaf =
  | { readonly __doodPropertyValue: string }
  | { readonly __doodDescriptorControlField: string };

/**
 * Recursive shape allowed at any node of a block config: a `propertyValue` leaf,
 * a `descriptorControlField` leaf, or a nested grouping of the same.
 */
export type DoodConfigShape =
  | { readonly __doodPropertyValue: string }
  | { readonly __doodDescriptorControlField: string }
  | { [key: string]: DoodConfigShape };

/**
 * Converts a camelCase path segment to the snake_case handle expected by the
 * legacy dashboard descriptor. Only the last segment of a dotted path is
 * converted, e.g. `colors.backgroundColor` -> `background_color`.
 */
export function toSnakeHandle(segment: string): string {
  return segment.replace(/([a-z0-9])([A-Z])/g, '$1_$2').toLowerCase();
}

export function lastSegment(path: string): string {
  const idx = path.lastIndexOf('.');
  return idx === -1 ? path : path.slice(idx + 1);
}

/** Resolves the CB1 field handle, honoring an optional `legacyHandle` override in options. */
export function resolveLegacyFieldHandle(
  path: string,
  options: Record<string, unknown> | undefined,
  insideGroup: boolean,
): string {
  if (options && typeof options.legacyHandle === 'string') {
    return options.legacyHandle;
  }
  return insideGroup ? lastSegment(path) : toSnakeHandle(lastSegment(path));
}

export type { DoodFieldType };
